import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ServerToAppSchema, type ServerToApp } from '@br/protocol';
import { patchProfileInCaches, patchProfileTabs, patchNodeOnline, qk } from '../api/queries';
import { useToast, type ToastLevel } from '../components/toast';
import { NOTIFICATION_TITLE } from '../lib/labels';
import { backoffDelay, wsUrl } from '../lib/ws';

const PING_MS = 20_000;

const NOTIFICATION_LEVEL: Record<string, ToastLevel> = {
  customer_done: 'success',
  customer_joined: 'info',
  control_requested: 'warning',
  node_offline: 'error',
  profile_error: 'error',
};

/**
 * Keeps one `/ws/app` socket open while an operator is logged in. Pushes server events into the
 * React Query caches and surfaces notifications as toasts.
 *
 * Notification policy: every notification becomes an in-app toast. A system `Notification` is
 * shown additionally only while this tab is hidden (`document.visibilityState !== 'visible'`), so
 * operators working in another window still hear about "고객 완료" without being double-notified
 * while looking at the dashboard. Permission is requested on the first click anywhere in the app.
 */
export function AppSocketProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!('Notification' in window) || Notification.permission !== 'default') return;
    const ask = () => {
      void Notification.requestPermission();
      window.removeEventListener('pointerdown', ask);
    };
    window.addEventListener('pointerdown', ask);
    return () => window.removeEventListener('pointerdown', ask);
  }, []);

  useEffect(() => {
    let ws: WebSocket | undefined;
    let stopped = false;
    let attempt = 0;
    let pingTimer: ReturnType<typeof setInterval> | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const handle = (msg: ServerToApp) => {
      switch (msg.t) {
        case 'node.state':
          patchNodeOnline(qc, msg.nodeId, msg.online);
          void qc.invalidateQueries({ queryKey: qk.nodes });
          return;
        case 'profile.state':
          patchProfileInCaches(qc, msg.profileId, (profile) => ({
            ...profile,
            runtime: msg.runtime,
            participantCount: msg.participantCount,
            controllerName: msg.controllerName ?? null,
          }));
          return;
        case 'support-session.state':
          void qc.invalidateQueries({ queryKey: qk.supportSessions(msg.profileId) });
          patchProfileInCaches(qc, msg.profileId, (profile) => ({
            ...profile,
            latestSupportSession:
              profile.latestSupportSession?.id === msg.supportSessionId
                ? { ...profile.latestSupportSession, status: msg.status }
                : profile.latestSupportSession,
          }));
          return;
        case 'tabs.changed':
          patchProfileTabs(qc, msg.profileId, msg.tabs);
          return;
        case 'notification': {
          const title = NOTIFICATION_TITLE[msg.kind] ?? msg.title;
          toast.push({
            title,
            message: msg.message,
            level: NOTIFICATION_LEVEL[msg.kind] ?? 'info',
            key: msg.profileId ? `${msg.kind}:${msg.profileId}` : undefined,
            actions: msg.profileId
              ? [{ label: '프로필 열기', onClick: () => navigate(`/profiles/${msg.profileId}`) }]
              : [],
          });
          if (msg.kind === 'node_offline') void qc.invalidateQueries({ queryKey: qk.nodes });
          if (
            'Notification' in window &&
            Notification.permission === 'granted' &&
            document.visibilityState !== 'visible'
          ) {
            try {
              const n = new Notification(title, {
                body: msg.message,
                tag: msg.profileId ?? msg.kind,
              });
              n.onclick = () => {
                window.focus();
                if (msg.profileId) navigate(`/profiles/${msg.profileId}`);
                n.close();
              };
            } catch {
              /* notifications unavailable in this context */
            }
          }
          return;
        }
        case 'pong':
          return;
      }
    };

    const connect = () => {
      if (stopped) return;
      ws = new WebSocket(wsUrl('/ws/app'));
      ws.onopen = () => {
        attempt = 0;
        pingTimer = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN)
            ws.send(JSON.stringify({ t: 'ping', ts: Date.now() }));
        }, PING_MS);
        // Anything that changed while we were disconnected
        void qc.invalidateQueries({ queryKey: ['profiles'] });
        void qc.invalidateQueries({ queryKey: qk.nodes });
      };
      ws.onmessage = (ev) => {
        if (typeof ev.data !== 'string') return;
        let json: unknown;
        try {
          json = JSON.parse(ev.data);
        } catch {
          return;
        }
        const parsed = ServerToAppSchema.safeParse(json);
        if (parsed.success) handle(parsed.data);
      };
      ws.onclose = (ev) => {
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = undefined;
        if (stopped) return;
        // 4401 = session gone; the next API call will redirect to /login, no point retrying fast
        const delay = ev.code === 4401 ? 30_000 : backoffDelay(attempt++);
        reconnectTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        /* onclose follows */
      };
    };
    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      ws?.close();
    };
  }, [qc, toast, navigate]);

  return <>{children}</>;
}
